import React, { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Minus, Package, ScanLine, Tag } from "lucide-react";
import { formatApiError } from "@/lib/api";
import BarcodeInput from "@/components/BarcodeInput";
import LabelPrintModal from "@/components/LabelPrintModal";
import { getPendingStockDeltas, subscribe as subscribeQueue } from "@/lib/offline";

const EMPTY = { manufacturer: "", model: "", year: "", variant: "", part_number: "", name: "", purchase_price: 0, mrp: 0, gst_rate: 18, bin_number: "", reorder_point: 5, reorder_quantity: 10 };

function PartModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const editing = !!initial?.id;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        purchase_price: Number(form.purchase_price),
        mrp: Number(form.mrp),
        gst_rate: Number(form.gst_rate),
        reorder_point: Number(form.reorder_point),
        reorder_quantity: Number(form.reorder_quantity),
      };
      if (editing) await api.patch(`/parts/${initial.id}`, payload);
      else await api.post("/parts", payload);
      toast.success(`Part ${editing ? "updated" : "created"}`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-300 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="part-modal">
        <div className="px-6 py-4 border-b border-zinc-200 sticky top-0 bg-white z-10">
          <div className="label-eyebrow">{editing ? "Edit" : "New"}</div>
          <h3 className="text-xl font-semibold text-zinc-950">{editing ? "Edit Part" : "Add Part"}</h3>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4">
          {[
            ["manufacturer", "Manufacturer", "text"],
            ["model", "Model", "text"],
            ["year", "Year", "text"],
            ["variant", "Variant", "text"],
            ["part_number", "Part Number", "text"],
            ["bin_number", "Bin / Location", "text"],
            ["name", "Description / Name", "text"],
            ["purchase_price", "Purchase Price (₹)", "number"],
            ["mrp", "MRP (₹)", "number"],
            ["gst_rate", "GST Rate (%)", "number"],
            ["reorder_point", "Reorder Point (low stock at)", "number"],
            ["reorder_quantity", "Suggested Reorder Qty", "number"],
          ].map(([k, label, type]) => (
            <div key={k} className={k === "name" ? "col-span-2" : ""}>
              <label className="label-eyebrow block mb-2">{label}</label>
              <input
                data-testid={`part-input-${k}`}
                type={type}
                value={form[k] ?? ""}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="w-full bg-white border border-zinc-300 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono"
                step={type === "number" ? "0.01" : undefined}
              />
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm uppercase tracking-wider hover:bg-zinc-50">Cancel</button>
          <button data-testid="part-save-button" disabled={busy} onClick={save} className="px-4 py-2 bg-zinc-950 text-white text-sm uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50">
            {busy ? "Saving..." : editing ? "Update Part" : "Create Part"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdjustModal({ part, onClose, onSaved }) {
  const [delta, setDelta] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/parts/${part.id}/adjust`, { delta: Number(delta), note });
      toast.success(`Stock adjusted by ${delta}`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-300 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-zinc-200">
          <div className="label-eyebrow">Inventory</div>
          <h3 className="text-xl font-semibold text-zinc-950">Adjust stock</h3>
          <p className="text-xs font-mono text-zinc-500 mt-1">{part.part_number} · current: {part.stock}</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label-eyebrow block mb-2">Delta (use negative to remove)</label>
            <input data-testid="adjust-delta-input" type="number" value={delta} onChange={(e) => setDelta(e.target.value)} className="w-full bg-white border border-zinc-300 text-sm px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Note</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full bg-white border border-zinc-300 text-sm px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" placeholder="e.g. restock from supplier" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm uppercase tracking-wider hover:bg-zinc-50">Cancel</button>
          <button data-testid="adjust-save-button" disabled={busy} onClick={save} className="px-4 py-2 bg-[#002FA7] text-white text-sm uppercase tracking-wider hover:bg-blue-900 disabled:opacity-50">Apply</button>
        </div>
      </div>
    </div>
  );
}

export default function Parts() {
  const { user } = useAuth();
  const [parts, setParts] = useState([]);
  const [q, setQ] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [facets, setFacets] = useState({ manufacturers: [], models: [] });
  const [edit, setEdit] = useState(null);
  const [adjust, setAdjust] = useState(null);
  const [open, setOpen] = useState(false);
  // Barcode-label multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [labelOpen, setLabelOpen] = useState(false);
  // Offline pending stock deltas — Map<part_id, qty>
  const [pending, setPending] = useState(new Map());

  const canEdit = ["admin", "inventory_manager"].includes(user.role);
  const showCost = user.role !== "retail_counter";

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (manufacturer) params.manufacturer = manufacturer;
    const [{ data: pts }, { data: fc }] = await Promise.all([
      api.get("/parts", { params }),
      api.get("/parts/facets"),
    ]);
    setParts(pts);
    setFacets(fc);
  };

  useEffect(() => { load(); }, [q, manufacturer]);

  // Keep pending-stock deltas fresh whenever the offline queue changes.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const m = await getPendingStockDeltas();
      if (!cancelled) setPending(m);
    };
    refresh();
    const unsub = subscribeQueue(refresh);
    return () => { cancelled = true; unsub(); };
  }, []);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setSelectedIds(new Set(parts.map((p) => p.id)));
  const clearSelection = () => setSelectedIds(new Set());
  const selectedParts = parts.filter((p) => selectedIds.has(p.id));

  const handleScan = async (code) => {
    try {
      const { data } = await api.get(`/parts/by-number/${encodeURIComponent(code)}`);
      toast.success(`Found: ${data.part_number}`);
      setQ(code);
    } catch (err) {
      if (err?.response?.status === 404) {
        if (canEdit) {
          toast.message(`No match for ${code}. Opening new part form…`);
          setEdit({ ...EMPTY, part_number: code });
          setOpen(true);
        } else {
          toast.error(`Part ${code} not in catalog`);
        }
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete part ${p.part_number}?`)) return;
    try {
      await api.delete(`/parts/${p.id}`);
      toast.success("Part deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="parts-root">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6">
        <div>
          <div className="label-eyebrow">Catalog</div>
          <h1 className="text-4xl font-bold tracking-tight text-zinc-950">Parts Catalog</h1>
          <p className="text-sm text-zinc-500 mt-2">Manufacturer → Model · Year → Part Number hierarchy.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <button data-testid="clear-selection-button" onClick={clearSelection} className="text-xs uppercase tracking-wider px-3 py-2 border border-zinc-300 bg-white hover:bg-zinc-50">
                Clear ({selectedIds.size})
              </button>
            )}
            <button
              data-testid="print-labels-button"
              onClick={() => setLabelOpen(true)}
              disabled={selectedIds.size === 0}
              className="text-xs uppercase tracking-wider px-3 py-2 border border-zinc-300 bg-white hover:bg-zinc-50 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Tag className="w-3.5 h-3.5" /> Print Labels
            </button>
            <button data-testid="add-part-button" onClick={() => { setEdit(null); setOpen(true); }} className="bg-zinc-950 text-white text-sm uppercase tracking-wider font-semibold px-4 py-2.5 hover:bg-zinc-800 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Part
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <div>
          <div className="label-eyebrow mb-1.5 flex items-center gap-2"><ScanLine className="w-3 h-3 text-[#002FA7]" />Barcode scan</div>
          <BarcodeInput
            testid="parts-barcode-input"
            placeholder="Scan part barcode…"
            hint={canEdit ? "Found → filters list · Not found → opens 'New part' form" : "Scan to find an existing part"}
            onScan={handleScan}
          />
        </div>
        <div>
          <div className="label-eyebrow mb-1.5">Search</div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="parts-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search part #, name, manufacturer..."
              className="w-full bg-white border border-zinc-300 text-sm pl-10 pr-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono"
            />
          </div>
        </div>
        <div>
          <div className="label-eyebrow mb-1.5">Filter</div>
          <select
            data-testid="parts-manufacturer-filter"
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
            className="bg-white border border-zinc-300 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono"
          >
            <option value="">All manufacturers</option>
            {facets.manufacturers.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>
      <div className="text-xs font-mono text-zinc-500 -mt-2">
        <span className="kbd">{parts.length}</span> records
      </div>

      <div className="brutalist-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              {canEdit && (
                <th className="text-left px-3 py-3 font-semibold w-8">
                  <input
                    data-testid="select-all-parts"
                    type="checkbox"
                    aria-label="Select all visible parts"
                    checked={parts.length > 0 && selectedIds.size === parts.length}
                    onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                  />
                </th>
              )}
              <th className="text-left px-5 py-3 font-semibold">Manufacturer</th>
              <th className="text-left px-5 py-3 font-semibold">Model / Variant / Year</th>
              <th className="text-left px-5 py-3 font-semibold">Part #</th>
              <th className="text-left px-5 py-3 font-semibold">Name</th>
              <th className="text-left px-5 py-3 font-semibold">Bin</th>
              {showCost && <th className="text-right px-5 py-3 font-semibold">Purchase</th>}
              <th className="text-right px-5 py-3 font-semibold">MRP</th>
              <th className="text-right px-5 py-3 font-semibold">GST%</th>
              {showCost && <th className="text-right px-5 py-3 font-semibold">Margin</th>}
              <th className="text-right px-5 py-3 font-semibold">Stock</th>
              {canEdit && <th className="text-right px-5 py-3 font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {parts.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 12 : 9} className="px-5 py-12 text-center text-sm text-zinc-500">
                  <Package className="w-6 h-6 mx-auto mb-2 text-zinc-300" />
                  No parts yet. {canEdit && "Click \"Add Part\" to create the first one."}
                </td>
              </tr>
            )}
            {parts.map((p) => {
              const pp = p.purchase_price ?? p.cost_price ?? 0;
              const mrp = p.mrp ?? p.retail_price ?? 0;
              const margin = mrp - pp;
              const marginPct = pp ? ((margin / pp) * 100).toFixed(1) : "—";
              const lowStock = p.stock <= (p.reorder_point ?? 5);
              const pendingQty = pending.get(p.id) || 0;
              return (
                <tr key={p.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`part-row-${p.part_number}`}>
                  {canEdit && (
                    <td className="px-3 py-3 w-8">
                      <input
                        data-testid={`select-part-${p.part_number}`}
                        type="checkbox"
                        aria-label={`Select ${p.part_number}`}
                        checked={selectedIds.has(p.id)}
                        onChange={() => toggleSelected(p.id)}
                      />
                    </td>
                  )}
                  <td className="px-5 py-3 text-zinc-950 font-medium">{p.manufacturer}</td>
                  <td className="px-5 py-3 text-zinc-700">{p.model}{p.variant && <span className="text-zinc-400"> · {p.variant}</span>} <span className="text-zinc-400 font-mono">· {p.year || "—"}</span></td>
                  <td className="px-5 py-3 font-mono text-zinc-950 font-medium">{p.part_number}</td>
                  <td className="px-5 py-3 text-zinc-700">{p.name || "—"}</td>
                  <td className="px-5 py-3 font-mono text-xs text-zinc-500">{p.bin_number || "—"}</td>
                  {showCost && <td className="px-5 py-3 text-right font-mono text-zinc-700">{fmtCurrency(pp)}</td>}
                  <td className="px-5 py-3 text-right font-mono text-zinc-950 font-semibold">{fmtCurrency(mrp)}</td>
                  <td className="px-5 py-3 text-right font-mono text-xs text-zinc-500">{p.gst_rate ?? 0}%</td>
                  {showCost && (
                    <td className="px-5 py-3 text-right font-mono">
                      <span className="text-emerald-700">{fmtCurrency(margin)}</span>
                      <span className="text-zinc-400 ml-1 text-xs">({marginPct}%)</span>
                    </td>
                  )}
                  <td className={`px-5 py-3 text-right font-mono font-semibold ${lowStock ? "text-rose-600" : "text-zinc-950"}`}>
                    {p.stock}
                    {pendingQty > 0 && (
                      <span
                        data-testid={`pending-badge-${p.part_number}`}
                        title={`${pendingQty} unit(s) queued in offline mutations`}
                        className="ml-1.5 inline-flex items-center px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 text-[10px] font-mono uppercase tracking-wider align-middle"
                      >
                        -{pendingQty} pending
                      </span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-5 py-3 text-right space-x-1">
                      <button data-testid={`part-adjust-${p.part_number}`} onClick={() => setAdjust(p)} className="p-1.5 border border-zinc-300 hover:bg-zinc-100 inline-flex"><Minus className="w-3.5 h-3.5" /></button>
                      <button data-testid={`part-edit-${p.part_number}`} onClick={() => { setEdit(p); setOpen(true); }} className="p-1.5 border border-zinc-300 hover:bg-zinc-100 inline-flex"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`part-delete-${p.part_number}`} onClick={() => remove(p)} className="p-1.5 border border-zinc-300 hover:bg-rose-50 hover:border-rose-300 inline-flex"><Trash2 className="w-3.5 h-3.5 text-rose-600" /></button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && <PartModal initial={edit} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {adjust && <AdjustModal part={adjust} onClose={() => setAdjust(null)} onSaved={() => { setAdjust(null); load(); }} />}
      {labelOpen && (
        <LabelPrintModal
          parts={selectedParts}
          onClose={() => setLabelOpen(false)}
        />
      )}
    </div>
  );
}
